namespace $.$$ {

	export class $shm_ba_user_settings extends $.$shm_ba_user_settings {

		limit_pending = new Map< string, number >()
		limit_saving = new Map< string, Promise< void > >()

		filter_text( value: string ) {
			return String( value ).replaceAll( '\\', '\\\\' ).replaceAll( '"', '\\"' )
		}

		override user_id() {
			return super.user_id() || this.$.$shm_ba_pb.user_id()
		}

		administrated() {
			return Boolean( this.$.$shm_ba_pb.admin_auth_token() )
		}

		settings_rows() {
			const wordstat = this.administrated() ? [
				this.Wordstat_enabled(),
				this.Wordstat_task_limit_label(),
				this.Wordstat_hourly_request_limit_label(),
				this.Wordstat_regular_daily_task_limit_label(),
				this.Wordstat_query_limit_label(),
				this.Wordstat_subtask_limit_label(),
			] : []
			if( this.scope() === 'wordstat' ) return wordstat
			if( this.scope() === 'agent' ) return [ this.Agent_settings() ]
			if( this.scope() === 'monitoring' ) return [ this.Mentions_enabled() ]
			if( this.scope() === 'mention_stats' ) return [ this.Mention_stats_enabled() ]
			return [
				... this.administrated() ? [ this.Mentions_enabled(), this.Mention_stats_enabled() ] : [],
				... wordstat,
				this.Agent_settings(),
			]
		}

		user() {
			const data = this.user_data() as $shm_ba_pb_file_record | null
			if( data ) return data

			const id = this.user_id().trim()
			if( !id ) return null
			return this.$.$shm_ba_pb.records_realtime( `users\nid = "${ this.filter_text( id ) }"` )[0] ?? null
		}

		setting< Value >( key: string, next: Value | undefined, fallback: Value ) {
			const user = this.user()
			if( next !== undefined ) {
				if( user?.id ) this.$.$shm_ba_pb.update_record( 'users', user.id, { [ key ]: next } )
				return next
			}
			return ( user?.[ key ] as Value | undefined ) ?? fallback
		}

		agent_enabled( next?: boolean ) {
			if( this.user_id() === this.$.$shm_ba_pb.user_id() ) return this.$.$shm_ba_pb.user_agent_enabled( next )
			return this.setting( 'agent_enabled', next, false )
		}

		wordstat_enabled( next?: boolean ) {
			return this.setting( 'wordstat_enabled', next, false )
		}

		mentions_enabled( next?: boolean ) {
			return this.setting( 'mentions_enabled', next, true )
		}

		mention_stats_enabled( next?: boolean ) {
			return this.setting( 'mention_stats_enabled', next, false )
		}

		wordstat_task_limit() {
			return Math.max( 0, Number( this.setting( 'wordstat_task_limit', undefined, 0 ) ) || 0 )
		}

		wordstat_hourly_request_limit() {
			return Math.max( 0, Number( this.setting( 'wordstat_hourly_request_limit', undefined, 1000 ) ) || 0 )
		}

		wordstat_regular_daily_task_limit() {
			return Math.max( 0, Number( this.setting( 'wordstat_regular_daily_task_limit', undefined, 100 ) ) || 0 )
		}

		wordstat_query_limit() {
			return Math.max( 1, Number( this.setting( 'wordstat_query_limit', undefined, 200 ) ) || 200 )
		}

		wordstat_subtask_limit() {
			return Math.max( 1, Number( this.setting( 'wordstat_subtask_limit', undefined, 1000 ) ) || 1000 )
		}

		@ $mol_mem_key
		wordstat_task_limit_draft_for( user_id: string, next?: string ) {
			$mol_wire_solid()
			return next ?? String( this.wordstat_task_limit() )
		}

		wordstat_task_limit_draft( next?: string ) {
			return this.wordstat_task_limit_draft_for( this.user_id(), next )
		}

		@ $mol_mem_key
		wordstat_hourly_request_limit_draft_for( user_id: string, next?: string ) {
			$mol_wire_solid()
			return next ?? String( this.wordstat_hourly_request_limit() )
		}

		wordstat_hourly_request_limit_draft( next?: string ) {
			return this.wordstat_hourly_request_limit_draft_for( this.user_id(), next )
		}

		@ $mol_mem_key
		wordstat_regular_daily_task_limit_draft_for( user_id: string, next?: string ) {
			$mol_wire_solid()
			return next ?? String( this.wordstat_regular_daily_task_limit() )
		}

		wordstat_regular_daily_task_limit_draft( next?: string ) {
			return this.wordstat_regular_daily_task_limit_draft_for( this.user_id(), next )
		}

		@ $mol_mem_key
		wordstat_query_limit_draft_for( user_id: string, next?: string ) {
			$mol_wire_solid()
			return next ?? String( this.wordstat_query_limit() )
		}

		wordstat_query_limit_draft( next?: string ) {
			return this.wordstat_query_limit_draft_for( this.user_id(), next )
		}

		@ $mol_mem_key
		wordstat_subtask_limit_draft_for( user_id: string, next?: string ) {
			$mol_wire_solid()
			return next ?? String( this.wordstat_subtask_limit() )
		}

		wordstat_subtask_limit_draft( next?: string ) {
			return this.wordstat_subtask_limit_draft_for( this.user_id(), next )
		}

		limit_value( value: string ) {
			return Math.max( 0, Math.floor( Number( value ) || 0 ) )
		}

		limit_save_id( user_id: string, key: string ) {
			return `${ user_id }\n${ key }`
		}

		async limit_save( save_id: string, user_id: string, key: string ) {
			while( this.limit_pending.has( save_id ) ) {
				const value = this.limit_pending.get( save_id )!
				await this.$.$shm_ba_pb.update_record_async( 'users', user_id, { [ key ]: value } )
				if( this.limit_pending.get( save_id ) === value ) this.limit_pending.delete( save_id )
			}
		}

		limit_save_start( key: string, value: number ) {
			const user_id = this.user()?.id
			if( !user_id ) return

			const save_id = this.limit_save_id( user_id, key )
			if( this.limit_pending.get( save_id ) === value ) return

			this.limit_pending.set( save_id, value )
			if( this.limit_saving.has( save_id ) ) return

			const saving = this.limit_save( save_id, user_id, key )
			this.limit_saving.set( save_id, saving )
			saving.catch( error => {
				this.limit_pending.delete( save_id )
				console.error( error )
			} ).finally( ()=> {
				if( this.limit_saving.get( save_id ) === saving ) this.limit_saving.delete( save_id )
			} )
		}

		@ $mol_action
		wordstat_task_limit_commit( next?: Event ) {
			if( next === undefined ) return null
			const value = this.limit_value( this.wordstat_task_limit_draft() )
			this.wordstat_task_limit_draft( String( value ) )
			if( value !== this.wordstat_task_limit() ) this.limit_save_start( 'wordstat_task_limit', value )
			return next
		}

		@ $mol_action
		wordstat_hourly_request_limit_commit( next?: Event ) {
			if( next === undefined ) return null
			const value = this.limit_value( this.wordstat_hourly_request_limit_draft() )
			this.wordstat_hourly_request_limit_draft( String( value ) )
			if( value !== this.wordstat_hourly_request_limit() ) this.limit_save_start( 'wordstat_hourly_request_limit', value )
			return next
		}

		@ $mol_action
		wordstat_regular_daily_task_limit_commit( next?: Event ) {
			if( next === undefined ) return null
			const value = this.limit_value( this.wordstat_regular_daily_task_limit_draft() )
			this.wordstat_regular_daily_task_limit_draft( String( value ) )
			if( value !== this.wordstat_regular_daily_task_limit() ) this.limit_save_start( 'wordstat_regular_daily_task_limit', value )
			return next
		}

		@ $mol_action
		wordstat_query_limit_commit( next?: Event ) {
			if( next === undefined ) return null
			const value = Math.max( 1, this.limit_value( this.wordstat_query_limit_draft() ) )
			this.wordstat_query_limit_draft( String( value ) )
			if( value !== this.wordstat_query_limit() ) this.limit_save_start( 'wordstat_query_limit', value )
			return next
		}

		@ $mol_action
		wordstat_subtask_limit_commit( next?: Event ) {
			if( next === undefined ) return null
			const value = Math.max( 1, this.limit_value( this.wordstat_subtask_limit_draft() ) )
			this.wordstat_subtask_limit_draft( String( value ) )
			if( value !== this.wordstat_subtask_limit() ) this.limit_save_start( 'wordstat_subtask_limit', value )
			return next
		}

		@ $mol_action
		wordstat_task_limit_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null
			next.preventDefault()
			this.wordstat_task_limit_draft( String( this.wordstat_task_limit() ) )
			this.Wordstat_task_limit().focused( false )
			return next
		}

		@ $mol_action
		wordstat_hourly_request_limit_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null
			next.preventDefault()
			this.wordstat_hourly_request_limit_draft( String( this.wordstat_hourly_request_limit() ) )
			this.Wordstat_hourly_request_limit().focused( false )
			return next
		}

		@ $mol_action
		wordstat_regular_daily_task_limit_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null
			next.preventDefault()
			this.wordstat_regular_daily_task_limit_draft( String( this.wordstat_regular_daily_task_limit() ) )
			this.Wordstat_regular_daily_task_limit().focused( false )
			return next
		}

		@ $mol_action
		wordstat_query_limit_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null
			next.preventDefault()
			this.wordstat_query_limit_draft( String( this.wordstat_query_limit() ) )
			this.Wordstat_query_limit().focused( false )
			return next
		}

		@ $mol_action
		wordstat_subtask_limit_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null
			next.preventDefault()
			this.wordstat_subtask_limit_draft( String( this.wordstat_subtask_limit() ) )
			this.Wordstat_subtask_limit().focused( false )
			return next
		}

		agent_url( next?: string ) {
			if( this.user_id() === this.$.$shm_ba_pb.user_id() ) return this.$.$shm_ba_pb.user_agent_url( next )
			return this.setting( 'agent_url', next, '' )
		}

		@ $mol_mem_key
		agent_url_draft_for( user_id: string, next?: string ) {
			$mol_wire_solid()
			return next ?? this.agent_url()
		}

		agent_url_draft( next?: string ) {
			return this.agent_url_draft_for( this.user_id(), next )
		}

		@ $mol_action
		agent_url_commit( next?: Event ) {
			if( next === undefined ) return null
			const value = this.agent_url_draft().trim()
			if( value !== this.agent_url() ) this.agent_url( value )
			this.agent_url_draft( value )
			return next
		}

		@ $mol_action
		agent_url_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null

			next.preventDefault()
			this.agent_url_draft( this.agent_url() )
			this.Agent_url().focused( false )

			return next
		}

		agent_token( next?: string ) {
			if( this.user_id() === this.$.$shm_ba_pb.user_id() ) return this.$.$shm_ba_pb.user_agent_token( next )
			return this.setting( 'agent_token', next, '' )
		}

		@ $mol_mem_key
		agent_token_draft_for( user_id: string, next?: string ) {
			$mol_wire_solid()
			return next ?? this.agent_token()
		}

		agent_token_draft( next?: string ) {
			return this.agent_token_draft_for( this.user_id(), next )
		}

		@ $mol_action
		agent_token_commit( next?: Event ) {
			if( next === undefined ) return null
			const value = this.agent_token_draft().trim()
			if( value !== this.agent_token() ) this.agent_token( value )
			this.agent_token_draft( value )
			return next
		}

		@ $mol_action
		agent_token_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null

			next.preventDefault()
			this.agent_token_draft( this.agent_token() )
			this.Agent_token().focused( false )

			return next
		}

		description() {
			return 'URL используется для opencode API, токен передается в opencode и MCP bridge.'
		}

	}

}
